import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { editUserInfo } from "~/services/server";
import { INTENTS, loadCookiesData } from "~/utils";
import * as APITypes from "~/API";
import { apiClient, OrgMemberDetailsLoaderResult } from "~/services/apiClient";
import { InstanceAccessTabAction } from "./InstanceAccessTabLoader";

export type MemberPageTabType =
  | "details"
  | "lifecycle-access"
  | "instance-access";

/**
 * Main loader for OrgMemberDetailsPage
 * Only fetches member and organization details
 * Each tab has its own loader for tab-specific data
 */
export async function OrgMemberDetailsPageLoader({
  request,
  params,
}: LoaderFunctionArgs): Promise<OrgMemberDetailsPageLoaderType | Response> {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType } = authAccessInfo.data;

  const organizationID = params.organizationID || "";
  const memberID = params.userID || "";

  const result = await apiClient.loader<OrgMemberDetailsLoaderResult>(
    "org-member-details",
    {
      organizationID,
      id: memberID,
      userID: user.uid,
    }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load org member details:", result.error);
    throw new Error(
      result.error ?? "Failed to load org member details"
    );
  }

  const data = result.data;
  const currentUserRole = authAccessInfo.data?.role;
  const canUpdateMemberRights =
    currentUserRole === APITypes.ManagerRolesType.SuperAdmin ||
    currentUserRole === APITypes.ManagerRolesType.Admin;

  return {
    memberDetails: data.memberDetails,
    organizationDetails: data.organizationDetails,
    ownerID,
    ownerType,
    userName: data.userName,
    canUpdateMemberRights,
    isSuperAdmin: currentUserRole === APITypes.ManagerRolesType.SuperAdmin,
  };
}

export type OrgMemberDetailsPageLoaderType = {
  memberDetails: APITypes.User;
  organizationDetails: APITypes.Organization;
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  canUpdateMemberRights: boolean;
  isSuperAdmin: boolean;
};

export const OrgMemberDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  const formData = await request.formData();
  const intent = formData.get("intent");
  const organizationID = params.organizationID || "";
  const memberID = params.userID || "";

  switch (intent) {
    case INTENTS._edit_member_details: {
      const payload: {
        email?: string;
        title?: string;
        phoneNumber?: string;
        roles?: APITypes.ManagerRolesType;
        _version?: number;
      } = JSON.parse(formData.get("body") as string);

      const updatePayload: APITypes.UpdateUserInput = {
        id: memberID,
        ...payload,
        _version: payload._version,
      };

      const result = await editUserInfo(updatePayload);
      return result;
    }

    case INTENTS._add_instance_access:
    case INTENTS._edit_instance_access:
    case INTENTS._remove_instance_access:
      return InstanceAccessTabAction({ request, params });

    case INTENTS._remove_from_organization: {
      // To be implemented
      console.log("Remove member from organization: To be implemented");
      return null;
    }

    default:
      return null;
  }
};
